import React, { useState, useReducer, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { Checkbox, Page } from '@superops/ui-design-system';
import { useIntl } from 'react-intl';
import {
  AccessControl,
  CustomModal,
  DetailCard,
  EventEmitter,
  EventEmitterWithIndex,
  getTranslatedText,
  preFormatForm,
  projectViewConstraints,
  reactSelectInlineError,
  SaveCard,
  validateForm,
  Loader
} from '@nile/components';
import AdvancedSearch from '@nile/components/src/Components/AdvancedSearch/AdvancedSearch';
import {
  onAttributeSelect,
  onValueSearch
} from '@nile/components/src/Pebbles/AdvancedProjectFilter';
import {
  GET_ENTITY_PROPERTIES,
  GET_RULEBUILDER_OPERATORS
} from '@nile/graphql/src';
import { GET_PROJECTS_LISTVIEW_META } from '../../Graphql/Query/projects';
import { pick } from 'lodash';

const ProjectViewModal = (props) => {
  const {
    isOpen,
    title,
    actions,
    actionvalue,
    isProjectViewLoading,
    ...restEditProps
  } = props;
  const intl = useIntl();

  const [projectViewName, setProjectViewName] = useState(
    restEditProps.projectViewName ?? ''
  );
  const [emojiData, setEmojiData] = useState(restEditProps.displayIcon ?? '😄');
  const [isSharedChecked, setIsSharedChecked] = useState(
    restEditProps.isSharedChecked ?? false
  );
  const [submitActionLoading, setSubmitActionLoading] = useState(false);

  const [viewCondition, setViewCondition] = useReducer(
    (state, newState) => ({
      ...state,
      ...newState
    }),
    restEditProps.viewCondition
      ? {
          ruleCondition: restEditProps.viewCondition
        }
      : {
          ruleCondition: { joinOperator: 'AND', operands: [] }
        }
  );
  const [errors, setErrors] = useState({});

  const { loading: entitiesLoading, data: allEntities } = useQuery(
    GET_ENTITY_PROPERTIES
  );

  const {
    loading: projectListViewMetaLoading,
    data: projectListViewMeta
  } = useQuery(GET_PROJECTS_LISTVIEW_META);

  const { loading: operatorsLoading, data: allOperators } = useQuery(
    GET_RULEBUILDER_OPERATORS
  );

  const handleCreateEditViewSave = () => {
    const input = {
      name: projectViewName,
      viewCondition: viewCondition.ruleCondition,
      shared: isSharedChecked,
      module: 'PROJECT',
      displayIcon: emojiData
    };

    const dummyForm = pick(input, ['name', 'shared']);
    // eslint-disable-next-line no-underscore-dangle
    const _dummyForm = preFormatForm(dummyForm);
    const validationResult = validateForm(
      { ..._dummyForm, viewCondition: viewCondition.ruleCondition },
      projectViewConstraints
    );

    if (validationResult) {
      return setErrors(validationResult);
    }
    setErrors({});

    setSubmitActionLoading(true);
    actions.primaryAction(input).finally((id) => {
      setSubmitActionLoading(false);
      actions.back();
      // ProjectList need to update when editing the project view
     if (actionvalue === 'edit') {
      restEditProps.viewType === 'card' ?
        EventEmitter.dispatch('refreshProjectList') : EventEmitterWithIndex.dispatch('refreshProjectColumns');
      }
    });
  };

  const updateSharedView = useCallback((selected) => {
    setIsSharedChecked(selected);
  });

  return (
    <CustomModal open={isOpen} onClose={actions.back}>
      <div className="vh80 vw70">
        {isProjectViewLoading ||
        projectListViewMetaLoading ||
        entitiesLoading ||
        operatorsLoading ? (
          <div className="fw fhp bg-white br10">
            <Loader />
          </div>
        ) : (
          <Page
            variant="primary"
            title={title}
            primaryAction={
              <SaveCard
                handleSave={handleCreateEditViewSave}
                actions={actions}
                loading={submitActionLoading}
                removeSecondaryBtnBg
              />
            }
            contentHeight="100px"
          >
            <DetailCard
              actions={{
                updateName: setProjectViewName,
                back: actions.back,
                updateEmoji: setEmojiData
              }}
              disableActive
              noDesciption
              notSectioned
              data={{ name: projectViewName, emoji: emojiData }}
              errors={errors?.name}
              useEmojiPicker={true}
              noCard
            >
              <AccessControl
                roleToCheck="projectSharedViews.allow"
                render={
                  <div className="mt-20">
                    <Checkbox
                      label={getTranslatedText(
                        intl,
                        'task.newView.checkbox.sharedView'
                      )}
                      id="projectSharedViews"
                      name="projectSharedViews"
                      checked={isSharedChecked}
                      onChange={updateSharedView}
                    />
                  </div>
                }
              />
            </DetailCard>
            <div className="mt40 mb-40">
              {!entitiesLoading &&
                !projectListViewMetaLoading &&
                !operatorsLoading && (
                  <AdvancedSearch
                    meta={projectListViewMeta.getProjectListViewMetaInfo}
                    allEntities={allEntities}
                    allOperators={allOperators}
                    context={{
                      updateAutomationRule: (formattedRule, key) => {
                        setViewCondition({ [key]: formattedRule });
                      }
                    }}
                    query={viewCondition.ruleCondition}
                    errors={errors?.['viewCondition.operands']}
                    currentModule="project"
                    onAttributeSelect={onAttributeSelect}
                    onValueSearch={onValueSearch}
                    isNewBuilder
                  />
                )}
              {typeof errors?.['viewCondition.operands']?.[0] === 'string' &&
                reactSelectInlineError(errors?.['viewCondition.operands'])}
            </div>
          </Page>
        )}
      </div>
    </CustomModal>
  );
};
export default ProjectViewModal;
